import { useSessionListVersion } from '@/components/layout/Navigation/sessions/hooks/useSessionListVersion';
import { useIsScheduleUploadPdfEnabled } from '@/features/flags';
import {
  isUploadSessionScheduleModalOpenAtom,
  scheduledSessionsDraftAtom,
  showPreviewAtom,
  uploadErrorsAtom,
} from '@/features/uploadSessionSchedule/atoms';
import {
  CSV_MAX_SESSIONS,
  FAILURE_ERROR_CODES,
} from '@/features/uploadSessionSchedule/const';
import { calculateUploadErrors } from '@/features/uploadSessionSchedule/helpers/calculateUploadErrors';
import {
  clearUploadAbortControllerAction,
  newUploadAbortControllerAction,
} from '@/features/uploadSessionSchedule/utils/uploadAbortControllerActions';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type ScheduleUploadEntryPoint } from '@/models/analytics/features/sessions';
import {
  FILE_UPLOAD_ERROR_CODES,
  type FileUploadAcceptedMeta,
  type FileUploadRejection,
} from '@heidi/design-system/file-upload';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { ScheduleUploadErrorCode } from '@heidi/schema/ml-scribe';
import {
  CSV_MAX_SIZE_BYTES,
  SESSION_SCHEDULE_ACCEPT_FORMATS,
  SESSION_SCHEDULE_ACCEPT_FORMATS_WITH_PDF,
} from '@heidi/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { resetPrechartImportUiState } from '../utils/prechart-correlation';
import { useMutateUploadSessionSchedule } from './useMutateUploadSessionSchedule';

const getFileType = (file: File): string | undefined => {
  const name = file.name.toLowerCase();
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex > 0 && dotIndex < name.length - 1) return name.slice(dotIndex + 1);
  return file.type ? file.type.split('/')[1] : undefined;
};

export const useScheduleUpload = ({
  entryPoint,
}: {
  entryPoint: ScheduleUploadEntryPoint;
}) => {
  const { t } = useTranslations();
  const intl = useIntl();
  // Attributed on upload analytics so dashboards can distinguish a dead input
  // method from a surface removed by a list-variant rollout (the consolidated
  // list has no sidebar dropzone).
  const sessionListVersion = useSessionListVersion();
  const scheduledSessionsDraft = useAtomValue(scheduledSessionsDraftAtom);
  const setDraftSessions = useSetAtom(scheduledSessionsDraftAtom);
  const setUploadErrors = useSetAtom(uploadErrorsAtom);
  const [isUploadSessionScheduleModalOpen, setIsUploadSessionScheduleModalOpen] = useAtom(
    isUploadSessionScheduleModalOpenAtom
  );
  const setShowPreview = useSetAtom(showPreviewAtom);
  const newUploadAbortController = useSetAtom(newUploadAbortControllerAction);
  const clearUploadAbortController = useSetAtom(clearUploadAbortControllerAction);
  const isPdfEnabled = useIsScheduleUploadPdfEnabled();
  const acceptFormats = isPdfEnabled
    ? SESSION_SCHEDULE_ACCEPT_FORMATS_WITH_PDF
    : SESSION_SCHEDULE_ACCEPT_FORMATS;

  const { mutateAsync: uploadSessionSchedule, isPending: isUploading } =
    useMutateUploadSessionSchedule();

  const getErrorMessageForErrorCode = useCallback(
    (errorCode?: ScheduleUploadErrorCode | null) => {
      if (!errorCode) return t('sessions.upcomingSessions.noValidSessionDataFound');
      switch (errorCode) {
        case ScheduleUploadErrorCode.invalid_time:
          return t('sessions.upcomingSessions.invalidTime');
        case ScheduleUploadErrorCode.invalid_date:
          return t('sessions.upcomingSessions.invalidDate');
        case ScheduleUploadErrorCode.date_outside_range:
          return t('sessions.upcomingSessions.dateOutsideRange');
        case ScheduleUploadErrorCode.conflict_overlap:
          return t('sessions.upcomingSessions.conflictOverlap');
        case ScheduleUploadErrorCode.missing_date_format:
          return t('sessions.upcomingSessions.missingDateFormat');
        case ScheduleUploadErrorCode.invalid_file:
          return t('sessions.upcomingSessions.invalidFile');
        case ScheduleUploadErrorCode.too_many_sessions:
          return t('sessions.upcomingSessions.tooManySessions', {
            max: CSV_MAX_SESSIONS,
          });
        default:
          return t('sessions.upcomingSessions.noValidSessionDataFound');
      }
    },
    [t]
  );

  const handleUploadError = useCallback((error: string) => {
    toast({
      tone: 'destructive',
      title: error,
    });
  }, []);

  const handleFilesRejected = useCallback(
    (rejections: FileUploadRejection[]) => {
      const firstRejection = rejections[0];
      if (!firstRejection) return;

      // Aggregate error codes across every rejected file — `TOO_MANY_FILES`
      // fires once per file in a multi-file drop, and the other codes can
      // co-occur with it.
      const errorCodes = new Set(rejections.flatMap((r) => r.errors.map((e) => e.code)));

      // Format errors take priority over size errors so users see
      // "unsupported format" first.
      if (errorCodes.has(FILE_UPLOAD_ERROR_CODES.FILE_INVALID_TYPE)) {
        handleUploadError(t('sessions.upcomingSessions.unsupportedFileFormat'));
        return;
      }
      if (errorCodes.has(FILE_UPLOAD_ERROR_CODES.FILE_TOO_LARGE)) {
        handleUploadError(t('sessions.upcomingSessions.fileTooLarge'));
        return;
      }
      if (errorCodes.has(FILE_UPLOAD_ERROR_CODES.TOO_MANY_FILES)) {
        handleUploadError(
          intl.formatMessage({
            defaultMessage: 'Please upload one file at a time.',
            id: 'FZ4cafkzxE',
          })
        );
      }
    },
    [handleUploadError, intl, t]
  );

  const handleFilesAccepted = useCallback(
    (files: File[], meta: FileUploadAcceptedMeta) => {
      const file = files[0];
      if (!file) {
        handleUploadError(t('sessions.upcomingSessions.noFileSelected'));
        return;
      }

      // Funnel top with method attribution — a silently dead input method shows
      // up as its share dropping to zero. Snowplow-only (`amplitude: false`):
      // the Snowplow pipeline forwards new events to Amplitude, so a direct
      // Amplitude call would double-count.
      track({
        event_name: TrackingEventName.ScheduleUploadStarted,
        props: {
          upload_method: meta.method,
          file_type: getFileType(file),
          entry_point: entryPoint,
          session_list_version: sessionListVersion,
        },
        options: { amplitude: false },
      });

      void (async () => {
        // Show preview immediately
        setShowPreview(true);
        resetPrechartImportUiState(scheduledSessionsDraft);
        setDraftSessions([]);
        setUploadErrors({
          failures: [],
          warnings: [],
          failureCount: 0,
          warningCount: 0,
        });

        const abortController = newUploadAbortController();

        if (!isUploadSessionScheduleModalOpen) {
          setIsUploadSessionScheduleModalOpen(true);
        }

        try {
          const response = await uploadSessionSchedule({
            file,
            signal: abortController.signal,
          });

          // Only update state if not cancelled
          if (!abortController.signal.aborted) {
            if (!response.sessions.length) {
              setShowPreview(false);
              const errorMessage = getErrorMessageForErrorCode(response.error);
              handleUploadError(errorMessage);
              return;
            }
            const allSessions = response.sessions.map((session, index) => ({
              ...session,
              index,
              is_selected: true,
            }));

            // Calculate upload errors
            const uploadErrors = calculateUploadErrors(allSessions);
            setUploadErrors(uploadErrors);

            // Filter failures out of draftSessions
            const validSessions = allSessions.filter(
              (session) =>
                !session.errors.some((error) => FAILURE_ERROR_CODES.includes(error))
            );
            setDraftSessions(validSessions);
          }
        } catch {
          // Only update state if not cancelled
          if (!abortController.signal.aborted) {
            setShowPreview(false);
          }
        } finally {
          // Clear the shared AbortController when upload completes
          clearUploadAbortController();
        }
      })();
    },
    [
      uploadSessionSchedule,
      setDraftSessions,
      setUploadErrors,
      t,
      getErrorMessageForErrorCode,
      setShowPreview,
      isUploadSessionScheduleModalOpen,
      setIsUploadSessionScheduleModalOpen,
      handleUploadError,
      clearUploadAbortController,
      newUploadAbortController,
      entryPoint,
      sessionListVersion,
      scheduledSessionsDraft,
    ]
  );

  return {
    acceptFormats,
    maxSize: CSV_MAX_SIZE_BYTES,
    isUploading,
    handleFilesAccepted,
    handleFilesRejected,
  };
};
